/**
 * Task Inbox Page
 *
 * Displays tasks assigned to, claimed by, or claimable by the current user.
 * Provides actions to claim, complete, unclaim, and cancel tasks.
 * Follows standard Nexo list view patterns (enum columns, date columns, field restrictions).
 */

import { useCallback, useMemo, useState } from 'react';
import {
  DataGridPremium,
  GridColDef,
  GridActionsCellItem,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PanTool from '@mui/icons-material/PanTool';
import Undo from '@mui/icons-material/Undo';
import Cancel from '@mui/icons-material/Cancel';
import OpenInNew from '@mui/icons-material/OpenInNew';
import {
  Box,
  Typography,
  Chip,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ViewToolbar } from '@/components/ViewToolbar';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { getErrorMessage } from '@/lib/apiClient';
import { GRID_ROW_HEIGHT } from '@/lib/gridStyles';
import { checkOfflineAndNotify } from '@/lib/offlineGuard';
import { formatDateTime } from '@/lib/locale';
import { getDateShortcutFilterOperators } from '@/lib/dateGridFilters';
import { useEnumColumns, getEnumFilterOperators } from '@/lib/enumGridFilters';
import { useFieldRestrictions } from '@/core/hooks/useFieldRestrictions';
import { WorkTask } from '@/types/taskmanagement/WorkTask';
import { resolveEntityUrl } from '@/lib/entityNavigation';
import {
  useMyTasks,
  useClaimTask,
  useUnclaimTask,
  useCompleteTask,
  useCancelTask,
} from '@/hooks/taskmanagement/useTaskInbox';

function parseOutcomes(availableOutcomes?: string): string[] {
  if (!availableOutcomes) return [];
  try {
    const parsed = JSON.parse(availableOutcomes);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export const TaskInboxPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const apiRef = useGridApiRef();
  const { restrictions } = useFieldRestrictions('TaskManagement', 'WorkTask');
  const [tab, setTab] = useState(0);
  const [apiErrors, setApiErrors] = useState<ValidationError[]>([]);

  // Complete dialog state
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [completeTaskItem, setCompleteTaskItem] = useState<WorkTask | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState('');
  const [comment, setComment] = useState('');

  // Query
  const { data, isLoading, refetch } = useMyTasks({
    maxResultCount: 100,
    sorting: 'priority desc, dueAt asc, creationTime desc',
  });

  // Mutations
  const claimMutation = useClaimTask();
  const unclaimMutation = useUnclaimTask();
  const completeMutation = useCompleteTask();
  const cancelMutation = useCancelTask();

  const handleError = useCallback((error: unknown) => {
    const errors = getValidationErrors(error);
    if (errors.length > 0) {
      setApiErrors(errors);
    } else {
      setApiErrors([{
        field: 'form',
        message: getErrorMessage(error),
        instancePath: '#',
        members: [],
      }]);
    }
  }, []);

  const handleClaim = useCallback((id: string) => {
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    claimMutation.mutate(id, { onError: handleError });
  }, [claimMutation, handleError, t]);

  const handleUnclaim = useCallback((id: string) => {
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    unclaimMutation.mutate(id, { onError: handleError });
  }, [unclaimMutation, handleError, t]);

  const handleCancel = useCallback((id: string) => {
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    cancelMutation.mutate(id, { onError: handleError });
  }, [cancelMutation, handleError, t]);

  const openCompleteDialog = useCallback((task: WorkTask) => {
    setCompleteTaskItem(task);
    const outcomes = parseOutcomes(task.availableOutcomes);
    setSelectedOutcome(outcomes[0] ?? '');
    setComment('');
    setCompleteDialogOpen(true);
  }, []);

  const handleComplete = useCallback(() => {
    if (!completeTaskItem || !selectedOutcome) return;
    if (checkOfflineAndNotify(setApiErrors, () => {}, t)) return;
    setApiErrors([]);
    completeMutation.mutate(
      { id: completeTaskItem.id, input: { outcome: selectedOutcome, comment: comment || undefined } },
      {
        onSuccess: () => {
          setCompleteDialogOpen(false);
          setCompleteTaskItem(null);
        },
        onError: handleError,
      }
    );
  }, [completeTaskItem, selectedOutcome, comment, completeMutation, handleError, t]);

  // Split tasks by tab
  const allTasks = data?.items ?? [];
  const activeTasks = useMemo(
    () => allTasks.filter(t => t.status !== 'COMPLETED' && t.status !== 'CANCELLED'),
    [allTasks]
  );
  const rows = tab === 0 ? activeTasks : allTasks;

  const actionsColumn: GridColDef = {
    field: '_rowActions',
    type: 'actions',
    headerName: 'Actions',
    width: 140,
    getActions: (params) => {
      const task = params.row as WorkTask;
      const actions: React.ReactElement[] = [];

      if (task.status === 'PENDING') {
        actions.push(
          <GridActionsCellItem
            key="claim"
            icon={<PanTool />}
            label="Claim"
            onClick={() => handleClaim(task.id)}
          />
        );
      }

      if (task.status === 'PENDING' || task.status === 'CLAIMED') {
        actions.push(
          <GridActionsCellItem
            key="complete"
            icon={<CheckCircle />}
            label="Complete"
            onClick={() => openCompleteDialog(task)}
          />
        );
      }

      if (task.status === 'CLAIMED') {
        actions.push(
          <GridActionsCellItem
            key="unclaim"
            icon={<Undo />}
            label="Release"
            onClick={() => handleUnclaim(task.id)}
            showInMenu
          />
        );
      }

      if (task.status !== 'COMPLETED' && task.status !== 'CANCELLED') {
        actions.push(
          <GridActionsCellItem
            key="cancel"
            icon={<Cancel />}
            label="Cancel"
            onClick={() => handleCancel(task.id)}
            showInMenu
          />
        );
      }

      // Navigate to TaskActionView for this task
      actions.push(
        <GridActionsCellItem
          key="viewTask"
          icon={<OpenInNew />}
          label="Open Task"
          onClick={() => navigate(`/taskmanagement/inbox/${task.id}`)}
        />
      );

      // Navigate to the source entity (if resolvable)
      const entityUrl = resolveEntityUrl(task.entityName, task.entityId);
      if (entityUrl) {
        actions.push(
          <GridActionsCellItem
            key="viewEntity"
            icon={<OpenInNew />}
            label={`View ${task.entityName || 'Entity'}`}
            onClick={() => navigate(entityUrl)}
            showInMenu
          />
        );
      }

      return actions;
    },
  } as GridColDef;

  const columns: GridColDef[] = [
    actionsColumn,
    {
      field: 'title',
      headerName: 'Task',
      type: 'string',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'status',
      headerName: 'Status',
      type: 'singleSelect',
      width: 150,
      filterOperators: getEnumFilterOperators('WorkTaskStatus'),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      type: 'singleSelect',
      width: 120,
      filterOperators: getEnumFilterOperators('WorkTaskPriority'),
    },
    {
      field: 'workflowName',
      headerName: 'Workflow',
      type: 'string',
      width: 160,
    },
    {
      field: 'stepName',
      headerName: 'Step',
      type: 'string',
      width: 120,
    },
    {
      field: 'entityName',
      headerName: 'Entity',
      type: 'string',
      width: 100,
      valueGetter: (_value: any, row: any) => row.entityName || row.entityType,
    },
    {
      field: 'dueAt',
      headerName: 'Due',
      type: 'dateTime',
      width: 180,
      valueGetter: (value: any, row: any) => {
        const fieldValue = value ?? row?.dueAt;
        if (!fieldValue) return null;
        try {
          const date = new Date(fieldValue);
          return isNaN(date.getTime()) ? null : date;
        } catch {
          return null;
        }
      },
      valueFormatter: (value: any) => {
        if (!value) return '';
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) return '';
        return formatDateTime(date);
      },
      filterOperators: getDateShortcutFilterOperators(true),
    },
    {
      field: 'isOverdue',
      headerName: 'Overdue',
      type: 'boolean',
      width: 80,
    },
    {
      field: 'selectedOutcome',
      headerName: 'Outcome',
      type: 'string',
      width: 120,
    },
    {
      field: 'creationTime',
      headerName: 'Created',
      type: 'dateTime',
      width: 180,
      valueGetter: (value: any, row: any) => {
        const fieldValue = value ?? row?.creationTime;
        if (!fieldValue) return null;
        try {
          const date = new Date(fieldValue);
          return isNaN(date.getTime()) ? null : date;
        } catch {
          return null;
        }
      },
      valueFormatter: (value: any) => {
        if (!value) return '';
        const date = value instanceof Date ? value : new Date(value);
        if (isNaN(date.getTime())) return '';
        return formatDateTime(date);
      },
      filterOperators: getDateShortcutFilterOperators(true),
    },
  ];

  // Enum field to enum type mapping for runtime loading
  const enumTypes: Record<string, string> = {
    'status': 'WorkTaskStatus',
    'priority': 'WorkTaskPriority',
  };

  // Enhance columns with runtime-loaded enum valueOptions
  const enhancedColumns = useEnumColumns(columns, enumTypes);

  // Apply field restrictions (HIDDEN fields are removed from the grid)
  const restrictedColumns = useMemo(() => {
    const cols = enhancedColumns;
    if (!restrictions || restrictions.size === 0) return cols;
    return cols.filter(col => restrictions.get(col.field) !== 'HIDDEN');
  }, [enhancedColumns, restrictions]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
      <ViewToolbar onRefresh={() => refetch()} />

      <ValidationErrorBanner
        errors={apiErrors}
        onExpand={() => {}}
        onClear={() => setApiErrors([])}
      />

      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          Task Inbox
        </Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
          <Tab label={`Active (${activeTasks.length})`} />
          <Tab label={`All (${allTasks.length})`} />
        </Tabs>

        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <DataGridPremium
            density="compact"
            rowHeight={GRID_ROW_HEIGHT}
            apiRef={apiRef}
            rows={rows}
            columns={restrictedColumns}
            loading={isLoading}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'priority', sort: 'desc' }] },
            }}
            filterDebounceMs={500}
            disableRowSelectionOnClick
            onRowDoubleClick={(params) => navigate(`/taskmanagement/inbox/${params.id}`)}
            getRowClassName={(params) =>
              params.row.isOverdue ? 'row-overdue' : ''
            }
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              '& .MuiDataGrid-root': {
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              },
              '& .MuiDataGrid-main': {
                flex: 1,
                overflow: 'auto',
              },
              '& .MuiDataGrid-virtualScroller': {
                flex: 1,
                overflow: 'auto',
              },
              '& .row-overdue': {
                bgcolor: 'error.main',
                color: 'error.contrastText',
                '&:hover': { bgcolor: 'error.dark' },
              },
            }}
          />
        </Box>
      </Box>

      {/* Complete Task Dialog */}
      <Dialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Complete Task</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {completeTaskItem?.title}
          </Typography>

          {completeTaskItem && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography variant="subtitle2">Select Outcome</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {parseOutcomes(completeTaskItem.availableOutcomes).map((outcome) => (
                  <Chip
                    key={outcome}
                    label={outcome}
                    color={selectedOutcome === outcome ? 'primary' : 'default'}
                    variant={selectedOutcome === outcome ? 'filled' : 'outlined'}
                    onClick={() => setSelectedOutcome(outcome)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Stack>

              {parseOutcomes(completeTaskItem.availableOutcomes).length === 0 && (
                <TextField
                  label="Outcome"
                  value={selectedOutcome}
                  onChange={(e) => setSelectedOutcome(e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
              )}

              <TextField
                label="Comment (optional)"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                size="small"
                fullWidth
                multiline
                rows={3}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleComplete}
            disabled={!selectedOutcome || completeMutation.isPending}
          >
            {completeMutation.isPending ? 'Completing...' : 'Complete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
